import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Flag, Pencil, Play, Plus, Trash2 } from 'lucide-react'
import type { Sprint, Taak } from '../../../gedeeld/types'
import { api } from '../../lib/api'
import { datum, meervoud, naarInvoer, opDringendheid, sleutelVan, startVanDag, vanInvoer } from '../../lib/opmaak'
import { meld, meldFout, nieuweTaak, openTaak, taakGewijzigd, useBasis } from '../../lib/staat'
import { Avatar, Badge, Kies, Knop, Leeg, Veld, Venster, vraagBevestiging } from '../ui'
import { Deadline, PrioriteitIcoon, SoortIcoon, StatusPil } from '../taak/stukjes'
import type { ProjectContext } from '../ProjectScherm'

/* ===========================================================================
 *  Backlog en sprints
 *
 *  Zoals in Jira: bovenaan wat nu loopt, daaronder wat gepland staat, en
 *  onderaan alles wat nog nergens in zit. Slepen van de backlog naar een
 *  sprint is plannen.
 *
 *  Een sprint afronden vraagt waar het onafgemaakte werk heen moet, en legt
 *  vast hoeveel er af was (0156). Die getallen blijven staan, ook als de
 *  taken daarna verschuiven -- dat is wat de snelheid onderaan betrouwbaar
 *  maakt.
 *
 *  De burndown is uitgerekend uit wanneer elke taak klaar ging (klaar_at),
 *  niet uit een dagelijkse momentopname. Een taak die later aan de sprint is
 *  toegevoegd telt dus vanaf het begin mee; dat is een vereenvoudiging, en
 *  hij staat hier zodat niemand er een conclusie aan hangt die hij niet kan
 *  dragen.
 * =========================================================================== */

export default function Backlog({ ctx }: { ctx: ProjectContext }) {
  const { projecten } = useBasis()
  const [bewerk, setBewerk] = useState<Sprint | 'nieuw' | null>(null)
  const [afronden, setAfronden] = useState<Sprint | null>(null)
  const [dicht, setDicht] = useState<Set<string>>(new Set())
  const [over, setOver] = useState<string | null>(null)

  const lopend = ctx.sprints.filter((s) => s.status === 'actief')
  const gepland = ctx.sprints.filter((s) => s.status === 'gepland').sort((a, b) => a.volgorde - b.volgorde)
  const afgerond = ctx.sprints.filter((s) => s.status === 'afgerond').sort((a, b) => (b.afgerondAt ?? 0) - (a.afgerondAt ?? 0))

  const inSprint = (id: string | null) => ctx.taken
    .filter((t) => (t.sprintId ?? null) === id && (id !== null || t.status !== 'klaar') && t.soort !== 'epic')
    .sort((a, b) => Number(a.status === 'klaar') - Number(b.status === 'klaar') || opDringendheid(a, b))

  async function verplaats(taakId: string, sprintId: string | null) {
    const t = ctx.taken.find((x) => x.id === taakId)
    if (!t || (t.sprintId ?? null) === sprintId) return
    const voor = ctx.taken
    ctx.setTaken((l) => l.map((x) => (x.id === taakId ? { ...x, sprintId } : x)))
    try {
      taakGewijzigd(await api.taakOpslaan({ id: taakId, sprintId }))
    } catch (e) {
      ctx.setTaken(() => voor)
      meldFout(e)
    }
  }

  async function start(s: Sprint) {
    try {
      const na = await api.sprintStarten(s.id)
      ctx.setSprints(ctx.sprints.map((x) => (x.id === na.id ? na : x)))
      meld(`${na.naam} is begonnen`)
    } catch (e) { meldFout(e) }
  }

  async function weg(s: Sprint) {
    if (!(await vraagBevestiging(`${s.naam} schrappen? De taken erin gaan terug naar de backlog.`, 'Schrappen'))) return
    try {
      await api.sprintVerwijderen(s.id)
      ctx.setSprints(ctx.sprints.filter((x) => x.id !== s.id))
      ctx.setTaken((l) => l.map((t) => (t.sprintId === s.id ? { ...t, sprintId: null } : t)))
    } catch (e) { meldFout(e) }
  }

  function vak(id: string | null, kop: React.ReactNode, acties: React.ReactNode, taken: Taak[], extra?: React.ReactNode) {
    const sleutel = id ?? 'backlog'
    const isDicht = dicht.has(sleutel)
    const punten = taken.reduce((n, t) => n + (t.punten ?? 0), 0)
    const klaar = taken.filter((t) => t.status === 'klaar').length
    return (
      <section
        className={`sprint-vak ${over === sleutel ? 'erboven' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setOver(sleutel) }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(null) }}
        onDrop={(e) => { e.preventDefault(); setOver(null); void verplaats(e.dataTransfer.getData('text/plain'), id) }}>
        <div className="sprint-vak-kop">
          <button type="button" className="mini" onClick={() => setDicht((d) => { const n = new Set(d); if (n.has(sleutel)) n.delete(sleutel); else n.add(sleutel); return n })}>
            {isDicht ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
          </button>
          {kop}
          <span className="stil">{meervoud(taken.length, 'taak', 'taken')}{punten ? ` · ${punten} punten` : ''}{id && klaar ? ` · ${klaar} klaar` : ''}</span>
          <span className="vul" />
          {acties}
        </div>
        {extra}
        {!isDicht && (
          <div className="sprint-taken">
            {taken.map((t) => (
              <div key={t.id} className={`backlog-regel ${t.status === 'klaar' ? 'af' : ''}`} draggable
                onDragStart={(e) => { e.dataTransfer.setData('text/plain', t.id); e.dataTransfer.effectAllowed = 'move' }}
                onClick={() => openTaak(t.id)}>
                <SoortIcoon soort={t.soort} grootte={13} />
                <span className="mono stil">{sleutelVan(t, projecten)}</span>
                <span className="regel-titel">{t.titel}</span>
                {t.ouderId && <span className="epic-pil">{ctx.taken.find((x) => x.id === t.ouderId)?.titel}</span>}
                <Deadline taak={t} kort />
                <StatusPil status={t.status} />
                <PrioriteitIcoon p={t.prioriteit} grootte={13} />
                <span className="punten">{t.punten ?? '–'}</span>
                <Avatar naam={t.toegewezenNaam} id={t.toegewezenAan} grootte={20} />
              </div>
            ))}
            {!taken.length && <p className="sprint-leeg">{id ? 'Sleep hier taken heen uit de backlog.' : 'Alles is ingepland, of er is nog niets.'}</p>}
          </div>
        )}
      </section>
    )
  }

  const snelheid = afgerond.slice(0, 6).map((s) => ({
    naam: s.naam,
    punten: ctx.taken.filter((t) => t.sprintId === s.id && t.status === 'klaar').reduce((n, t) => n + (t.punten ?? 0), 0),
    klaar: s.afgerondKlaar ?? 0,
    open: s.afgerondOpen ?? 0,
  })).reverse()

  return (
    <div className="backlog">
      {lopend.map((s) => vak(s.id,
        <><Badge kleur="ok">Loopt</Badge><strong>{s.naam}</strong><span className="stil">{datum(s.startAt)} – {datum(s.eindAt)}</span></>,
        <>
          <Knop klein soort="stil" icoon={<Pencil size={14} />} onClick={() => setBewerk(s)} />
          <Knop klein icoon={<Flag size={14} />} onClick={() => setAfronden(s)}>Afronden</Knop>
        </>,
        inSprint(s.id),
        <>
          {s.doel && <p className="sprint-doel-lang">{s.doel}</p>}
          <Burndown sprint={s} taken={inSprint(s.id)} />
        </>,
      ))}

      {gepland.map((s, i) => vak(s.id,
        <><Badge kleur="info">Gepland</Badge><strong>{s.naam}</strong>
          {(s.startAt || s.eindAt) && <span className="stil">{datum(s.startAt)} – {datum(s.eindAt)}</span>}</>,
        <>
          <Knop klein soort="stil" icoon={<Pencil size={14} />} onClick={() => setBewerk(s)} />
          <Knop klein soort="stil" icoon={<Trash2 size={14} />} onClick={() => void weg(s)} />
          {i === 0 && !lopend.length && <Knop klein soort="hoofd" icoon={<Play size={14} />} onClick={() => void start(s)}>Starten</Knop>}
        </>,
        inSprint(s.id),
        s.doel ? <p className="sprint-doel-lang">{s.doel}</p> : undefined,
      ))}

      {vak(null,
        <strong>Backlog</strong>,
        <>
          <Knop klein soort="stil" icoon={<Plus size={14} />} onClick={() => nieuweTaak({ projectId: ctx.project.id, sprintId: null })}>Taak</Knop>
          <Knop klein icoon={<Plus size={14} />} onClick={() => setBewerk('nieuw')}>Sprint plannen</Knop>
        </>,
        inSprint(null),
      )}

      {!!afgerond.length && (
        <section className="vlak">
          <div className="vlak-kop"><h2>Eerdere sprints</h2></div>
          {snelheid.length > 1 && <Snelheid rijen={snelheid} />}
          <div className="regels">
            {afgerond.map((s) => (
              <div key={s.id} className="regel stil-regel">
                <strong>{s.naam}</strong>
                <span className="stil">{datum(s.startAt)} – {datum(s.afgerondAt)}</span>
                <span className="vul" />
                <span>{s.afgerondKlaar ?? 0} klaar</span>
                <span className="stil">{s.afgerondOpen ?? 0} doorgeschoven</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {!ctx.sprints.length && !ctx.taken.length && (
        <Leeg titel="Nog niets te plannen" tekst="Maak eerst een paar taken, of laat de AI een doel opsplitsen in het tabblad AI." />
      )}

      {bewerk && (
        <SprintVenster sprint={bewerk === 'nieuw' ? null : bewerk} projectId={ctx.project.id}
          aantal={ctx.sprints.length}
          laatsteEind={Math.max(0, ...ctx.sprints.map((s) => s.eindAt ?? 0))}
          onSluit={() => setBewerk(null)}
          onKlaar={(s) => {
            ctx.setSprints(ctx.sprints.some((x) => x.id === s.id) ? ctx.sprints.map((x) => (x.id === s.id ? s : x)) : [...ctx.sprints, s])
            setBewerk(null)
          }} />
      )}
      {afronden && (
        <AfrondVenster sprint={afronden} gepland={gepland} taken={inSprint(afronden.id)}
          onSluit={() => setAfronden(null)}
          onKlaar={() => { setAfronden(null); ctx.herlaad() }} />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  De burndown
 * ------------------------------------------------------------------ */

function Burndown({ sprint, taken }: { sprint: Sprint; taken: Taak[] }) {
  const lijnen = useMemo(() => {
    if (!sprint.startAt || !sprint.eindAt) return null
    const begin = startVanDag(sprint.startAt)
    const eind = startVanDag(sprint.eindAt)
    const dagen = Math.max(1, Math.round((eind - begin) / 86400000))
    /* Zonder punten tellen we taken; dat is beter dan een lege grafiek. */
    const metPunten = taken.some((t) => t.punten)
    const gewicht = (t: Taak) => (metPunten ? t.punten ?? 0 : 1)
    const totaal = taken.reduce((n, t) => n + gewicht(t), 0)
    const vandaag = Math.min(dagen, Math.floor((startVanDag(Date.now()) - begin) / 86400000))
    const echt: number[] = []
    for (let d = 0; d <= Math.max(0, vandaag); d++) {
      const grens = begin + (d + 1) * 86400000
      const klaar = taken.filter((t) => t.status === 'klaar' && (t.klaarAt ?? 0) < grens).reduce((n, t) => n + gewicht(t), 0)
      echt.push(totaal - klaar)
    }
    return { dagen, totaal, echt, eenheid: metPunten ? 'punten' : 'taken' }
  }, [sprint.startAt, sprint.eindAt, taken])

  if (!lijnen || !lijnen.totaal) return null
  const B = 560, H = 120, m = 24
  const x = (d: number) => m + (d / lijnen.dagen) * (B - m * 2)
  const y = (v: number) => 8 + (1 - v / lijnen.totaal) * (H - 24)
  const echt = lijnen.echt.map((v, d) => `${x(d)},${y(v)}`).join(' ')
  const nu = lijnen.echt[lijnen.echt.length - 1] ?? lijnen.totaal

  return (
    <div className="burndown">
      <svg viewBox={`0 0 ${B} ${H}`} role="img" aria-label={`Burndown: nog ${nu} ${lijnen.eenheid} van ${lijnen.totaal}`}>
        <line x1={x(0)} y1={y(lijnen.totaal)} x2={x(lijnen.dagen)} y2={y(0)} className="bd-ideaal" />
        <polyline points={echt} className="bd-echt" />
        {lijnen.echt.map((v, d) => <circle key={d} cx={x(d)} cy={y(v)} r={2.5} className="bd-punt" />)}
        <line x1={m} y1={H - 16} x2={B - m} y2={H - 16} className="bd-as" />
      </svg>
      <span className="stil">Nog <strong>{nu}</strong> van {lijnen.totaal} {lijnen.eenheid}. De stippellijn is het tempo dat nodig is.</span>
    </div>
  )
}

function Snelheid({ rijen }: { rijen: { naam: string; punten: number; klaar: number; open: number }[] }) {
  const metPunten = rijen.some((r) => r.punten)
  const max = Math.max(1, ...rijen.map((r) => (metPunten ? r.punten : r.klaar)))
  return (
    <div className="snelheid" role="img" aria-label="Wat er per sprint af kwam">
      {rijen.map((r) => {
        const v = metPunten ? r.punten : r.klaar
        return (
          <div key={r.naam} className="snelheid-kolom" title={`${r.naam}: ${v} ${metPunten ? 'punten' : 'taken'} af`}>
            <span className="snelheid-getal">{v}</span>
            <div className="snelheid-staaf" style={{ height: `${(v / max) * 72}px` }} />
            <span className="snelheid-naam">{r.naam}</span>
          </div>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Vensters
 * ------------------------------------------------------------------ */

function SprintVenster({ sprint, projectId, aantal, laatsteEind, onSluit, onKlaar }: {
  sprint: Sprint | null; projectId: string; aantal: number; laatsteEind: number
  onSluit: () => void; onKlaar: (s: Sprint) => void
}) {
  /* Een nieuwe sprint begint waar de vorige ophield, en duurt twee weken. */
  const startStandaard = laatsteEind > Date.now() ? laatsteEind + 86400000 : Date.now()
  const [naam, setNaam] = useState(sprint?.naam ?? `Sprint ${aantal + 1}`)
  const [doel, setDoel] = useState(sprint?.doel ?? '')
  const [start, setStart] = useState(naarInvoer(sprint?.startAt ?? startStandaard))
  const [eind, setEind] = useState(naarInvoer(sprint?.eindAt ?? startStandaard + 13 * 86400000))
  const [bezig, setBezig] = useState(false)

  async function bewaar() {
    setBezig(true)
    try {
      onKlaar(await api.sprintOpslaan({
        id: sprint?.id, projectId, naam, doel, startAt: vanInvoer(start), eindAt: vanInvoer(eind),
      }))
    } catch (e) { meldFout(e) }
    setBezig(false)
  }

  return (
    <Venster titel={sprint ? sprint.naam : 'Sprint plannen'} onSluit={onSluit}
      voet={<><span className="vul" /><Knop soort="stil" onClick={onSluit}>Annuleren</Knop>
        <Knop soort="hoofd" bezig={bezig} disabled={!naam.trim()} onClick={() => void bewaar()}>Opslaan</Knop></>}>
      <div className="formulier">
        <Veld label="Naam" breed><input className="invoer" value={naam} onChange={(e) => setNaam(e.target.value)} autoFocus /></Veld>
        <Veld label="Doel" breed hulp="Eén zin over wat er aan het eind anders is. Dat is waar je in de terugblik op toetst.">
          <textarea className="invoer" rows={2} value={doel} onChange={(e) => setDoel(e.target.value)} />
        </Veld>
        <Veld label="Begint"><input type="date" className="invoer" value={start} onChange={(e) => setStart(e.target.value)} /></Veld>
        <Veld label="Eindigt"><input type="date" className="invoer" value={eind} onChange={(e) => setEind(e.target.value)} /></Veld>
      </div>
    </Venster>
  )
}

function AfrondVenster({ sprint, gepland, taken, onSluit, onKlaar }: {
  sprint: Sprint; gepland: Sprint[]; taken: Taak[]; onSluit: () => void; onKlaar: () => void
}) {
  const open = taken.filter((t) => t.status !== 'klaar')
  const [naar, setNaar] = useState<string>(gepland[0]?.id ?? '')
  const [bezig, setBezig] = useState(false)

  async function rond() {
    setBezig(true)
    try {
      const uit = await api.sprintAfronden(sprint.id, naar || null)
      meld(`${sprint.naam} afgerond: ${uit.klaar} klaar, ${uit.open} doorgeschoven`)
      onKlaar()
    } catch (e) { meldFout(e) }
    setBezig(false)
  }

  return (
    <Venster titel={`${sprint.naam} afronden`} onSluit={onSluit}
      voet={<><span className="vul" /><Knop soort="stil" onClick={onSluit}>Annuleren</Knop>
        <Knop soort="hoofd" bezig={bezig} onClick={() => void rond()}>Afronden</Knop></>}>
      <p>
        <strong>{taken.length - open.length}</strong> van de {taken.length} taken zijn klaar.
        {open.length > 0 && <> Er {open.length === 1 ? 'is er 1' : `zijn er ${open.length}`} niet af.</>}
      </p>
      {open.length > 0 && (
        <div className="formulier">
          <Veld label="Wat niet af is gaat naar" breed>
            <Kies<string> waarde={naar} onKies={setNaar} opties={[
              { key: '', label: 'De backlog' },
              ...gepland.map((s) => ({ key: s.id, label: s.naam })),
            ]} />
          </Veld>
        </div>
      )}
    </Venster>
  )
}
